import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

const Privacy = () => {
  return (
    <div className="terms-container">
      <div className="terms-wrapper">
        <Link to="/" className="back-link">
          <Button variant="ghost" className="back-button">
            <ArrowLeft className="back-icon" />
            Back to Home
          </Button>
        </Link>

        <div className="terms-content">
          <h1 className="terms-title">Yappr Privacy Policy</h1>
          <p className="effective-date">Effective Date: December 11, 2025</p>

          <p className="intro-text">
            At Yappr Inc. ("we," "us," or "our"), we value your privacy. This Privacy Policy explains how we collect, use, share, and protect your personal information when you use our location-based social media app and related services (the "Service"). By using the Service, you consent to the practices described here.
          </p>

          <section className="terms-section">
            <h2>1. Information We Collect</h2>
            <p>We collect the following types of information:</p>
            
            <p><strong>Account Information:</strong> Name, email address, username, profile picture, and password when you register.</p>
            
            <p><strong>User Content:</strong> Text, photos, videos, and metadata (e.g., timestamps) in your "Yaps." Note that most Yaps are deleted after 5 days, but event-related or restaurant picture Yaps are retained.</p>
            
            <p><strong>Location Data:</strong> Precise or approximate location from your device to enable features like nearby content discovery. We may infer location from IP addresses or other data.</p>
            
            <p><strong>Device and Usage Data:</strong> Device type, OS, IP address, browser type, app interactions, and analytics data (e.g., pages viewed, time spent).</p>
            
            <p><strong>Contact Information:</strong> If you connect your contacts or social networks, we may collect names and contact details to suggest connections.</p>
            
            <p><strong>Other Data:</strong> Information you provide in feedback, surveys, or support requests.</p>

            <p>We do not collect sensitive information like financial data unless necessary for specific features (e.g., in-app purchases, if applicable).</p>
          </section>

          <section className="terms-section">
            <h2>2. How We Use Your Information</h2>
            <p>We use your information to:</p>
            <ul>
              <li>Provide and improve the Service (e.g., showing relevant Yaps based on location).</li>
              <li>Personalize your experience (e.g., recommending content or users).</li>
              <li>Communicate with you (e.g., notifications, updates, support).</li>
              <li>Analyze usage and trends to enhance features.</li>
              <li>Enforce our Terms and prevent fraud or abuse.</li>
              <li>Comply with legal obligations.</li>
            </ul>
            <p>For retained Yaps (events or restaurant pictures), we use the data to enable searching and archival features.</p>
          </section>

          <section className="terms-section">
            <h2>3. Sharing Your Information</h2>
            <p>We share information as follows:</p>
            
            <p><strong>With Other Users:</strong> Your profile, Yaps, and location (if shared) are visible to others based on your privacy settings.</p>
            
            <p><strong>Service Providers:</strong> Third parties who help us operate the Service (e.g., cloud storage, analytics like Google Analytics), bound by confidentiality.</p>
            
            <p><strong>Legal Requirements:</strong> If required by law, subpoena, or to protect rights, safety, or property.</p>
            
            <p><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets.</p>
            
            <p><strong>Aggregated Data:</strong> Non-personal, anonymized data for research or marketing.</p>

            <p>We do not sell your personal information to third parties.</p>
          </section>

          <section className="terms-section">
            <h2>4. Data Retention</h2>
            <p>
              Most Yaps and associated data are deleted after 5 days. Retained Yaps (events or restaurant pictures) are kept indefinitely unless you delete them. Account data is retained while your account is active and for a reasonable period after deletion for legal or backup purposes.
            </p>
          </section>

          <section className="terms-section">
            <h2>5. Your Rights and Choices</h2>
            
            <p><strong>Access and Update:</strong> View and edit your account information in the app.</p>
            
            <p><strong>Delete Data:</strong> Request deletion of your account or specific data via support@yappr.com. Note: Retained Yaps may remain visible if shared publicly.</p>
            
            <p><strong>Location Controls:</strong> Disable location sharing in device settings.</p>
            
            <p><strong>Opt-Out:</strong> From marketing emails or push notifications via app settings.</p>
            
            <p><strong>California Residents:</strong> Under CCPA/CPRA, you have rights to access, delete, and opt-out of sales (though we don't sell data). Contact us for requests.</p>
            
            <p><strong>EU/UK Residents:</strong> Under GDPR/UK GDPR, you have rights to access, rectify, erase, restrict, or object to processing. Contact our Data Protection Officer at dpo@yappr.com.</p>
          </section>

          <section className="terms-section">
            <h2>6. Security</h2>
            <p>
              We use industry-standard measures (e.g., encryption, firewalls) to protect your data. However, no system is completely secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section className="terms-section">
            <h2>7. Children's Privacy</h2>
            <p>
              The Service is not intended for children under 13. We do not knowingly collect data from them. If we discover such data, we delete it.
            </p>
          </section>

          <section className="terms-section">
            <h2>8. International Transfers</h2>
            <p>
              Your data may be transferred to and stored in the United States or other countries with different privacy laws. We ensure appropriate safeguards (e.g., standard contractual clauses).
            </p>
          </section>

          <section className="terms-section">
            <h2>9. Changes to Privacy Policy</h2>
            <p>
              We may update this Policy. Changes will be posted in the app, and material changes notified via email. Continued use constitutes acceptance.
            </p>
          </section>

          <section className="terms-section">
            <h2>10. Contact Us</h2>
            <p className="contact-info">
              For questions or requests, email <a href="mailto:support@umenuhub.com">support@umenuhub.com</a>.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Privacy;